import { useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"

import { ApiRequestError } from "@/hooks/api/base"
import { useApiContext } from "@/hooks/api/use-api-context"

import {
  createCalendarEvent,
  CreateCalendarEventParams,
  deleteCalendarEvent,
  rescheduleCalendarEvent,
  RescheduleParams,
  rsvpCalendarEvent,
  updateCalendarEvent,
  UpdateCalendarEventParams,
} from "./api"

function isForbiddenError(error: Error): boolean {
  return error instanceof ApiRequestError && error.status === 403
}

function isProviderPermissionError(error: Error): boolean {
  if (!(error instanceof ApiRequestError)) return false

  const body = error.responseBody as { email_integration_id?: string } | undefined
  return Boolean(body?.email_integration_id)
}

function calendarEditPermissionErrorToast(error: Error, fallback: string): string | null {
  if (!isForbiddenError(error)) return fallback
  if (isProviderPermissionError(error)) return null

  return "You don't have permission to edit this calendar."
}

export function useRescheduleCalendarEvent() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: RescheduleParams) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return rescheduleCalendarEvent(context, params)
    },
    meta: {
      errorToast: (error: Error) => calendarEditPermissionErrorToast(error, "Failed to reschedule meeting"),
    },
    onSuccess: () => {
      toast.success("Meeting rescheduled")
      queryClient.invalidateQueries({ queryKey: ["meetings"] })
      queryClient.invalidateQueries({ queryKey: ["calendar-events"] })
    },
  })
}

export function useCreateCalendarEvent() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: CreateCalendarEventParams) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return createCalendarEvent(context, params)
    },
    meta: {
      errorToast: (error: Error) => calendarEditPermissionErrorToast(error, "Failed to create event"),
    },
    onSuccess: () => {
      toast.success("Event created")
      queryClient.invalidateQueries({ queryKey: ["calendar-events"] })
      queryClient.invalidateQueries({ queryKey: ["meetings"] })
    },
  })
}

export function useUpdateCalendarEvent() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...params }: UpdateCalendarEventParams & { id: string }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return updateCalendarEvent(context, id, params)
    },
    meta: {
      errorToast: (error: Error) => calendarEditPermissionErrorToast(error, "Failed to update event"),
    },
    onSuccess: () => {
      toast.success("Event updated")
      queryClient.invalidateQueries({ queryKey: ["calendar-events"] })
    },
  })
}

export function useRsvpCalendarEvent() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ calendarEventId, status }: { calendarEventId: string; status: "yes" | "no" | "maybe" }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return rsvpCalendarEvent(context, calendarEventId, status)
    },
    meta: { errorToast: { fallback: "Failed to update RSVP" } },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["calendar-events"] })
    },
  })
}

export function useDeleteCalendarEvent() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, destroyOnProvider }: { id: string; destroyOnProvider: boolean }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return deleteCalendarEvent(context, id, destroyOnProvider)
    },
    meta: {
      errorToast: (error: Error) => calendarEditPermissionErrorToast(error, "Failed to delete event"),
    },
    onSuccess: () => {
      toast.success("Event deleted")
      queryClient.invalidateQueries({ queryKey: ["calendar-events"] })
    },
  })
}
